import { Phone, Video } from "lucide-react";
import type { JSX } from "react";

import type { ChannelDTO, MeDTO } from "../../../common/api/types.js";
import { Avatar, PRESENCE_LABELS } from "../../../common/components/Avatar.js";

/** Kopf des Chat-Panels: mit wem man schreibt, und wie man ihn erreicht. */
export function ChatHeader({
  channel,
  me,
  connected,
  busy,
  onCall,
}: {
  channel: ChannelDTO;
  me: MeDTO;
  connected: boolean;
  busy: boolean;
  onCall: (audioOnly: boolean) => void;
}): JSX.Element {
  const other = channel.members.find((member) => member.userId !== me.user.id);
  const isGroup = channel.kind === "GROUP";

  const subtitle = isGroup
    ? `${channel.members.length} Mitglieder`
    : other
      ? [PRESENCE_LABELS[other.presence], other.isAgent ? "Agent" : null].filter(Boolean).join(" · ")
      : "";

  return (
    <header className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-2.5">
      <Avatar
        displayName={other?.displayName ?? channel.title}
        avatarUrl={other?.avatarUrl}
        presence={isGroup ? null : (other?.presence ?? null)}
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{channel.title}</p>
        <p className="truncate text-xs text-ink-muted">{subtitle}</p>
      </div>

      <span
        title={connected ? "Verbunden" : "Nicht verbunden"}
        className={["h-2 w-2 shrink-0 rounded-full", connected ? "bg-green-500" : "bg-ink-muted"].join(" ")}
      >
        <span className="sr-only">{connected ? "Verbunden" : "Nicht verbunden"}</span>
      </span>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onCall(true)}
          disabled={busy}
          title="Anrufen — nur Ton"
          className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink disabled:opacity-50"
        >
          <Phone className="h-4 w-4" />
          <span className="sr-only">Anrufen</span>
        </button>
        <button
          type="button"
          onClick={() => onCall(false)}
          disabled={busy}
          title="Meeting mit Bild und Bildschirmfreigabe"
          className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink disabled:opacity-50"
        >
          <Video className="h-4 w-4" />
          <span className="sr-only">Meeting starten</span>
        </button>
      </div>
    </header>
  );
}
